// Downscales + re-encodes a picked photo before it ever touches storage or
// the network. Applied both online and offline: it shrinks Cloudinary
// payloads in the normal case, and keeps the local IndexedDB footprint small
// enough to matter when a photo has to sit queued on-device until sync.
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.8;

// `accept` des inputs photo : sans les extensions explicites, certains
// sélecteurs de fichiers (Windows notamment) masquent les .heic.
export const IMAGE_ACCEPT = 'image/*,.heic,.heif';

const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];

export function isHeicFile(file) {
    return HEIC_TYPES.includes(file.type) || /\.(heic|heif)$/i.test(file.name || '');
}

// Safari décode le HEIC nativement ; Chrome, Edge et Firefox non. Dans ce cas
// seulement on charge heic-to (~3 Mo, d'où l'import dynamique) pour le décoder.
async function decodeImage(file) {
    try {
        // imageOrientation: 'from-image' bakes in the EXIF rotation so the
        // canvas draw below doesn't produce a sideways photo (canvas drawImage
        // ignores EXIF on its own).
        return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (err) {
        if (!isHeicFile(file)) throw err;
        const { heicTo } = await import('heic-to');
        return heicTo({ blob: file, type: 'bitmap' });
    }
}

export async function compressImageFile(file, { maxDimension = MAX_DIMENSION, quality = JPEG_QUALITY } = {}) {
    const bitmap = await decodeImage(file);
    try {
        const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
        const width = Math.round(bitmap.width * scale);
        const height = Math.round(bitmap.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
        if (!blob) throw new Error("Échec de l'encodage JPEG de l'image");
        return blob;
    } finally {
        bitmap.close();
    }
}
